// api/bulkUpload.service.js
import API from './axiosConfig';

/**
 * Get destinations for current client (for dropdown)
 */
export const getClientDestinations = async () => {
    try {
        const response = await API.get('/api/bulk-upload/destinations');
        console.log('Destinations fetched:', response.data);
        return response.data;
    } catch (error) {
        console.error('Error in getClientDestinations:', error);
        return {
            status: false,
            message: error.response?.data?.message || error.message,
            data: []
        };
    }
};

/**
 * Bulk upload activities from Excel file
 * @param {FormData} formData - FormData containing file and destination_id
 */
export const bulkUploadActivities = async (formData) => {
    try {
        // Log to check what's being sent (for debugging)
        console.log('📤 Bulk Upload Request:');
        for (let pair of formData.entries()) {
            console.log(`   ${pair[0]}:`, pair[1] instanceof File ? `File: ${pair[1].name}` : pair[1]);
        }
        
        const response = await API.post('/api/bulk-upload/activities', formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });
        
        console.log('📥 Bulk upload response:', response.data);
        return response.data;
    } catch (error) {
        console.error('❌ Bulk upload error:', error);
        console.error('Error response:', error.response?.data);
        
        // Throw error with meaningful message
        throw {
            status: false,
            message: error.response?.data?.message || error.message || 'Bulk upload failed',
            errors: error.response?.data?.errors || null
        };
    }
};

/**
 * Add single activity
 * @param {Object} data - Activity data
 */
export const addSingleActivity = async (data) => {
    try {
        console.log('Adding single activity:', data);
        const response = await API.post('/api/bulk-upload/activity', data);
        return response.data;
    } catch (error) {
        console.error('Error in addSingleActivity:', error);
        return {
            status: false,
            message: error.response?.data?.message || error.message
        };
    }
};

/**
 * Download Excel template for bulk upload
 */
export const downloadTemplate = async () => {
    try {
        console.log('Downloading template...');
        const response = await API.get('/api/bulk-upload/template', {
            responseType: 'blob'
        });
        
        // Create blob and download
        const blob = new Blob([response.data], { 
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
        });
        
        const downloadUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.setAttribute('download', 'activities_template.xlsx');
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(downloadUrl);
        
        console.log('Template downloaded successfully');
        return { status: true };
    } catch (error) {
        console.error('Error in downloadTemplate:', error);
        return {
            status: false,
            message: error.response?.data?.message || error.message
        };
    }
};

/**
 * Get activities by destination ID
 * @param {number} destinationId - Destination ID
 */
export const getActivitiesByDestination = async (destinationId) => {
    try {
        console.log(`Fetching activities for destination ${destinationId}...`);
        const response = await API.get(`/api/bulk-upload/activities/${destinationId}`);
        return response.data;
    } catch (error) {
        console.error('Error fetching activities:', error);
        throw {
            status: false,
            message: error.response?.data?.message || error.message
        };
    }
};

/**
 * Update an existing activity
 * @param {number} activityId - Activity ID
 * @param {Object} data - Updated activity data
 */
export const updateActivity = async (activityId, data) => {
    try {
        console.log(`Updating activity ${activityId}:`, data);
        const response = await API.put(`/api/bulk-upload/activity/${activityId}`, data);
        return response.data;
    } catch (error) {
        console.error('Error updating activity:', error);
        throw {
            status: false,
            message: error.response?.data?.message || error.message
        };
    }
};

/**
 * Delete an activity
 * @param {number} activityId - Activity ID
 */
export const deleteActivity = async (activityId) => {
    try {
        console.log(`Deleting activity ${activityId}...`);
        const response = await API.delete(`/api/bulk-upload/activity/${activityId}`);
        return response.data;
    } catch (error) {
        console.error('Error deleting activity:', error);
        throw {
            status: false,
            message: error.response?.data?.message || error.message
        };
    }
};

/**
 * Bulk delete activities
 * @param {Array} activityIds - Array of activity IDs to delete
 */
export const bulkDeleteActivities = async (activityIds) => {
    try {
        console.log(`Bulk deleting activities:`, activityIds);
        const response = await API.post('/api/bulk-upload/activities/bulk-delete', { activity_ids: activityIds });
        return response.data;
    } catch (error) {
        console.error('Error bulk deleting activities:', error);
        throw {
            status: false,
            message: error.response?.data?.message || error.message
        };
    }
};

/**
 * Export activities to Excel
 * @param {number} destinationId - Destination ID
 */
export const exportActivitiesToExcel = async (destinationId) => {
    try {
        console.log(`Exporting activities for destination ${destinationId}...`);
        const response = await API.get(`/api/bulk-upload/activities/${destinationId}/export`, {
            responseType: 'blob'
        });
        
        // Create blob and download
        const blob = new Blob([response.data], { 
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
        });
        
        const downloadUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.setAttribute('download', `activities_destination_${destinationId}.xlsx`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(downloadUrl);
        
        console.log('Activities exported successfully');
        return { status: true };
    } catch (error) {
        console.error('Error exporting activities:', error);
        return {
            status: false,
            message: error.response?.data?.message || error.message
        };
    }
};

/**
 * Validate Excel file before upload
 * @param {File} file - Excel file to validate
 */
export const validateExcelFile = async (file) => {
    try {
        const formData = new FormData();
        formData.append('file', file);
        
        const response = await API.post('/api/bulk-upload/validate', formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });
        
        return response.data;
    } catch (error) {
        console.error('Error validating Excel file:', error);
        throw {
            status: false,
            message: error.response?.data?.message || error.message
        };
    }
};

/**
 * Get upload history
 * @param {number} page - Page number
 * @param {number} limit - Items per page
 */
export const getUploadHistory = async (page = 1, limit = 10) => {
    try {
        const response = await API.get(`/api/bulk-upload/history?page=${page}&limit=${limit}`);
        return response.data;
    } catch (error) {
        console.error('Error fetching upload history:', error);
        throw {
            status: false,
            message: error.response?.data?.message || error.message
        };
    }
};

/**
 * Get upload status by ID
 * @param {string} uploadId - Upload ID
 */
export const getUploadStatus = async (uploadId) => {
    try {
        const response = await API.get(`/api/bulk-upload/status/${uploadId}`);
        return response.data;
    } catch (error) {
        console.error('Error fetching upload status:', error);
        throw {
            status: false,
            message: error.response?.data?.message || error.message
        };
    }
};